import React, { useState } from "react"
import { Link } from "react-router-dom"
import SEO from "../components/SEO.jsx"

const CONTACT_ENDPOINT = "https://forms.2merative.com/api/2merative-contact.php?form=website-enquiry"

const proofItems = [
  {
    title: "Natural Healing",
    image: "/assets/blog/logos/landingpage/NaturalHealing.png",
    url: "https://natural-healing.life/",
  },
  {
    title: "Sturt Marion Thunder",
    image: "/assets/blog/logos/landingpage/SturtMarion.png",
    url: "https://www.sturtmarionthunder.com.au/",
  },
  {
    title: "Great Southern Chiropractic",
    image: "/assets/blog/logos/landingpage/GreatSouthern.png",
    url: "https://greatsouthernchiropractic.com/",
  },
]

export default function WebsiteLanding() {
  const [status, setStatus] = useState("idle")
  const [formData, setFormData] = useState({
    name: "",
    business: "",
    email: "",
    phone: "",
    website: "",
    message: "",
  })

  function handleChange(event) {
    const { name, value } = event.target

    setFormData((current) => ({
      ...current,
      [name]: value,
    }))
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setStatus("submitting")

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          business: formData.business,
          email: formData.email,
          phone: formData.phone,
          website: formData.website,
          message: formData.message,
          source: "Website Landing Page",
        }),
      })

      if (!response.ok) {
        throw new Error("Form submission failed")
      }

      window.location.href = "/thank-you"
    } catch (error) {
      setStatus("error")
    }
  }

  return (
    <div className="bg-blue-950 text-white">
      <SEO
        title="Free Website Review Adelaide | 2merative"
        description="Free website review for Adelaide businesses. Find out what is stopping your website from generating enquiries."
        path="/website-landing"
        image="/og-image.png"
      />

      <main>
        <section className="px-4 pt-28 pb-16 sm:pt-32 sm:pb-20">
          <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
            <div>
              <p className="mb-4 inline-flex rounded-full border border-blue-300/30 bg-blue-400/10 px-4 py-2 text-sm font-medium text-blue-200">
                Free website review for Adelaide businesses
              </p>

              <h1 className="mb-5 max-w-3xl text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
                Websites That Convert Visitors Into Enquiries
              </h1>

              <p className="mb-3 max-w-2xl text-xl font-semibold text-blue-100">
                Built for Adelaide businesses. Most projects start from $3,000.
              </p>

              <p className="mb-6 max-w-2xl text-lg leading-8 text-slate-300">
                We show what is stopping your website from generating enquiries
                and how to fix it.
              </p>

              <div className="mb-8 grid gap-3 text-sm text-slate-200 sm:grid-cols-3">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  Find what’s costing you leads
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  Clear actions to fix it
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  Built for Adelaide businesses
                </div>
              </div>

              <div className="rounded-3xl border border-blue-300/20 bg-blue-400/10 p-5">
                <h2 className="mb-3 text-xl font-semibold text-white">
                  What we check
                </h2>

                <ul className="grid gap-3 text-sm leading-6 text-slate-200 sm:grid-cols-2">
                  <li>• Whether your message is clear</li>
                  <li>• Whether visitors know what to do next</li>
                  <li>• Whether the page builds trust fast</li>
                  <li>• Whether mobile users can act easily</li>
                  <li>• Whether your CTA is strong enough</li>
                  <li>• Whether the site is costing you leads</li>
                </ul>
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white p-5 text-slate-900 shadow-2xl sm:p-7">
              <p className="mb-3 rounded-full bg-blue-50 px-4 py-2 text-center text-xs font-semibold text-blue-800">
                Trusted by Adelaide businesses
              </p>

              <h2 className="mb-2 text-2xl font-bold text-blue-950">
                Get My Free Website Review
              </h2>

              <p className="mb-5 text-sm leading-6 text-slate-600">
                Tell us about your business and we will review your website and
                show you what is stopping it from generating enquiries.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label
                    className="mb-1 block text-sm font-medium text-slate-800"
                    htmlFor="name"
                  >
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600"
                  />
                </div>

                <div>
                  <label
                    className="mb-1 block text-sm font-medium text-slate-800"
                    htmlFor="business"
                  >
                    Business name
                  </label>
                  <input
                    id="business"
                    name="business"
                    type="text"
                    value={formData.business}
                    onChange={handleChange}
                    required
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600"
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      className="mb-1 block text-sm font-medium text-slate-800"
                      htmlFor="email"
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600"
                    />
                  </div>

                  <div>
                    <label
                      className="mb-1 block text-sm font-medium text-slate-800"
                      htmlFor="phone"
                    >
                      Phone
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600"
                    />
                  </div>
                </div>

                <div>
                  <label
                    className="mb-1 block text-sm font-medium text-slate-800"
                    htmlFor="website"
                  >
                    Current website
                  </label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    value={formData.website}
                    onChange={handleChange}
                    required
                    placeholder="yourwebsite.com.au"
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600"
                  />
                </div>

                <div>
                  <label
                    className="mb-1 block text-sm font-medium text-slate-800"
                    htmlFor="message"
                  >
                    What is not working?
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us what is frustrating you about your current website."
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600"
                  />
                </div>

                {status === "error" && (
                  <p className="text-sm text-red-600">
                    Something went wrong. Please try again.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full rounded-full bg-blue-700 px-6 py-3 font-semibold text-white shadow-lg transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {status === "submitting"
                    ? "Sending..."
                    : "Get My Free Website Review"}
                </button>
              </form>

              <p className="mt-4 text-center text-xs text-slate-500">
                Limited spots each week due to manual reviews.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-white px-4 py-16 text-slate-900">
          <div className="mx-auto max-w-6xl">
            <div className="mb-10 max-w-3xl">
              <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-blue-700">
                The problem
              </p>
              <h2 className="mb-4 text-3xl font-bold text-blue-950">
                Most websites do not fail because they look bad.
              </h2>
              <p className="text-lg leading-8 text-slate-700">
                They fail because visitors cannot quickly understand the offer,
                trust the business, or take the next step. That means missed
                calls, missed enquiries, and missed work.
              </p>
            </div>

            <div className="grid gap-5 md:grid-cols-3">
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
                <h3 className="mb-2 text-lg font-semibold text-blue-950">
                  Unclear message
                </h3>
                <p className="text-sm leading-6 text-slate-700">
                  Visitors land on the page but do not instantly understand why
                  they should choose you.
                </p>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
                <h3 className="mb-2 text-lg font-semibold text-blue-950">
                  Weak call to action
                </h3>
                <p className="text-sm leading-6 text-slate-700">
                  The page does not make the next step obvious, easy, and worth
                  taking.
                </p>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
                <h3 className="mb-2 text-lg font-semibold text-blue-950">
                  Not built for leads
                </h3>
                <p className="text-sm leading-6 text-slate-700">
                  A nice-looking site can still leak enquiries if the structure
                  is wrong.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-slate-100 px-4 py-16 text-slate-900">
          <div className="mx-auto max-w-6xl">
            <div className="mb-10 text-center">
              <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-blue-700">
                What you receive
              </p>
              <h2 className="text-3xl font-bold text-blue-950">
                A clear review you can act on
              </h2>
            </div>

            <div className="grid gap-5 md:grid-cols-4">
              <div className="rounded-3xl bg-white p-6 shadow-sm">
                <p className="mb-3 text-sm font-bold text-blue-700">01</p>
                <h3 className="mb-2 font-semibold text-blue-950">
                  First impression review
                </h3>
                <p className="text-sm leading-6 text-slate-700">
                  We check whether your website builds trust quickly.
                </p>
              </div>

              <div className="rounded-3xl bg-white p-6 shadow-sm">
                <p className="mb-3 text-sm font-bold text-blue-700">02</p>
                <h3 className="mb-2 font-semibold text-blue-950">
                  Lead flow review
                </h3>
                <p className="text-sm leading-6 text-slate-700">
                  We check whether the path to enquiry is clear.
                </p>
              </div>

              <div className="rounded-3xl bg-white p-6 shadow-sm">
                <p className="mb-3 text-sm font-bold text-blue-700">03</p>
                <h3 className="mb-2 font-semibold text-blue-950">
                  Mobile review
                </h3>
                <p className="text-sm leading-6 text-slate-700">
                  We check whether mobile visitors can act easily.
                </p>
              </div>

              <div className="rounded-3xl bg-white p-6 shadow-sm">
                <p className="mb-3 text-sm font-bold text-blue-700">04</p>
                <h3 className="mb-2 font-semibold text-blue-950">
                  Fix list
                </h3>
                <p className="text-sm leading-6 text-slate-700">
                  You receive clear priorities, not vague advice.
                </p>
              </div>
            </div>

            <p className="mx-auto mt-8 max-w-3xl text-center text-sm leading-6 text-slate-700">
              If your site needs work, we can rebuild it properly. No
              obligation.
            </p>
          </div>
        </section>

        <section className="bg-white px-4 py-16 text-slate-900">
          <div className="mx-auto max-w-6xl">
            <div className="mb-10 text-center">
              <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-blue-700">
                Proof
              </p>
              <h2 className="text-3xl font-bold text-blue-950">
                Real websites built for real businesses
              </h2>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {proofItems.map((item) => (
                <article
                  key={item.title}
                  className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
                >
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block bg-slate-100"
                  >
                    <img
                      src={item.image}
                      alt={`${item.title} website preview`}
                      className="h-52 w-full object-cover"
                      loading="lazy"
                    />
                  </a>

                  <div className="p-5">
                    <h3 className="mb-3 text-lg font-semibold text-blue-950">
                      {item.title}
                    </h3>

                    <a
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-semibold text-blue-700 hover:underline"
                    >
                      View website →
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-blue-950 px-4 py-16 text-white">
          <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-[0.9fr_1.1fr] md:items-center">
            <div>
              <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-blue-200">
                Next step
              </p>
              <h2 className="mb-4 text-3xl font-bold">
                Find out what is stopping your website from converting.
              </h2>
              <p className="text-slate-300">
                You do not need to rebuild your website blindly. Start with a
                clear review and fix the right things first.
              </p>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/10 p-6">
              <a
                href="#name"
                className="inline-flex w-full justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-blue-950 shadow-lg transition hover:bg-blue-50"
              >
                Get My Free Website Review
              </a>

              <p className="mt-4 text-center text-xs text-blue-100">
                Free review for Adelaide businesses. Most projects start from
                $3,000.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}